/**
 * In-app status messages (replaces corner toasts for most UX feedback).
 * OnScreenStatusBar subscribes; installOnScreenNotify patches react-toastify.
 */

let seq = 0;
const listeners = new Set();
/** @type {Map<string|number, object>} */
const messages = new Map();

function emit() {
  const list = Array.from(messages.values()).sort((a, b) => a.createdAt - b.createdAt);
  listeners.forEach((fn) => {
    try { fn(list); } catch { /* ignore */ }
  });
}

export function subscribeStatusMessages(fn) {
  listeners.add(fn);
  fn(Array.from(messages.values()).sort((a, b) => a.createdAt - b.createdAt));
  return () => listeners.delete(fn);
}

function normalizeContent(content) {
  if (content == null) return '';
  if (typeof content === 'string') return content;
  if (typeof content === 'number' || typeof content === 'boolean') return String(content);
  if (ReactIsElement(content)) return ''; // ignore React nodes — fall back to toast
  try {
    return String(content);
  } catch {
    return '';
  }
}

function ReactIsElement(x) {
  return Boolean(x && typeof x === 'object' && x.$$typeof);
}

/**
 * @param {'info'|'success'|'error'|'warning'|'loading'} type
 * @param {unknown} content
 * @param {object} [options]
 * @returns {string|number|null} id, or null if caller should use toast fallback
 */
export function pushStatusMessage(type, content, options = {}) {
  const text = normalizeContent(content);
  if (!text.trim()) return null;

  const id = options.toastId ?? options.id ?? `status-${++seq}`;
  const persistent = options.autoClose === false || type === 'loading';
  const autoClose = persistent
    ? false
    : (typeof options.autoClose === 'number' ? options.autoClose : (type === 'error' ? 8000 : 5000));

  const prev = messages.get(id);
  if (options.toastId && prev) {
    clearTimeout(prev._timer);
  }

  const entry = {
    id,
    type,
    message: text,
    createdAt: prev?.createdAt || Date.now(),
    persistent,
    _timer: null,
  };
  messages.set(id, entry);
  emit();

  if (autoClose !== false && autoClose > 0) {
    entry._timer = setTimeout(() => dismissStatusMessage(id), autoClose);
  }
  return id;
}

export function updateStatusMessage(id, { type, render, autoClose, ...rest } = {}) {
  const prev = messages.get(id);
  if (!prev) {
    if (render != null) return pushStatusMessage(type || 'info', render, { toastId: id, autoClose, ...rest });
    return id;
  }
  if (prev._timer) clearTimeout(prev._timer);

  const nextType = type || (rest.isLoading ? 'loading' : prev.type);
  const text = render != null ? normalizeContent(render) : prev.message;
  const persistent = autoClose === false || nextType === 'loading';
  const closeMs = persistent
    ? false
    : (typeof autoClose === 'number' ? autoClose : (nextType === 'error' ? 8000 : 5000));

  const entry = {
    ...prev,
    type: nextType === 'default' ? 'info' : nextType,
    message: text || prev.message,
    persistent,
    _timer: null,
  };
  messages.set(id, entry);
  emit();

  if (closeMs !== false && closeMs > 0) {
    entry._timer = setTimeout(() => dismissStatusMessage(id), closeMs);
  }
  return id;
}

export function dismissStatusMessage(id) {
  if (id == null) {
    messages.forEach((m) => { if (m._timer) clearTimeout(m._timer); });
    messages.clear();
    emit();
    return;
  }
  const prev = messages.get(id);
  if (prev?._timer) clearTimeout(prev._timer);
  messages.delete(id);
  emit();
}

export function getStatusMessages() {
  return Array.from(messages.values()).sort((a, b) => a.createdAt - b.createdAt);
}
